import React, { useState } from 'react';
import {
  Plus,
  Trash2,
  ChevronRight,
  FileText,
  Edit as EditIcon,
  AlertCircle,
  ArrowUp,
  ArrowDown,
  Copy,
  X as CloseIcon,
  ArrowLeft,
  ChevronDown,
  Layout,
  Type,
  Settings2,
  Save,
  Loader2,
  Check
} from 'lucide-react';
import { updateProjectQuestionnaire } from '../../services/teamhaven';

interface QuestionnaireBuilderProps {
  projectId: number;
  questionnaire: any;
  onUpdate: (newQ: any) => void;
  onSave?: (currentQ?: any) => Promise<void>;
}

const FIELD_CLS =
  'w-full text-xs font-medium bg-white border border-[#e9e9e9] rounded-[9px] px-3 py-2 text-[#1e2a32] focus:ring-1 focus:ring-[#96c36d] outline-none transition-all';
const FIELD_LABEL_CLS =
  'text-[10px] font-bold text-slate-400 block mb-2';

export const QuestionnaireBuilder: React.FC<QuestionnaireBuilderProps> = ({
  projectId,
  questionnaire,
  onUpdate,
  onSave
}) => {
  const [selectedFormIdx, setSelectedFormIdx] = useState<number | null>(null);
  const [selectedQuestionIdx, setSelectedQuestionIdx] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const update = (patch: any) => {
    onUpdate({ ...questionnaire, ...patch });
  };

  const handleAddForm = () => {
    const newQ = { ...questionnaire };
    if (!newQ.Forms) newQ.Forms = [];
    
    const newForm = {
      Caption: { DefaultCaption: `Form ${newQ.Forms.length + 1}` },
      Type: 1, // Survey form
      DeleteRole: 3,
      ModifyRole: 3,
      Questions: []
    };
    
    const signOffIndex = newQ.Forms.findIndex((f: any) => f.Caption?.DefaultCaption === 'Sign Off');
    if (signOffIndex !== -1) {
      newQ.Forms.splice(signOffIndex, 0, newForm);
      onUpdate(newQ);
      setSelectedFormIdx(signOffIndex);
    } else {
      newQ.Forms.push(newForm);
      onUpdate(newQ);
      setSelectedFormIdx(newQ.Forms.length - 1);
    }
    setSelectedQuestionIdx(null);
  };

  const handleDeleteForm = (fIdx: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${questionnaire.Forms[fIdx]?.Caption?.DefaultCaption || 'this form'}" and all its questions?`)) return;
    const newQ = { ...questionnaire };
    newQ.Forms.splice(fIdx, 1);
    onUpdate(newQ);
    setSelectedFormIdx(null);
    setSelectedQuestionIdx(null);
  };

  const handleAddQuestion = (formIdx: number) => {
    const newQ = { ...questionnaire };
    if (!newQ.Forms[formIdx].Questions) newQ.Forms[formIdx].Questions = [];

    newQ.Forms[formIdx].Questions.push({
      Caption: { DefaultCaption: 'New Question' },
      Type: 1, // Text by default
      Required: false,
      ReadOnly: false
    });
    onUpdate(newQ);
    setSelectedFormIdx(formIdx);
    setSelectedQuestionIdx(newQ.Forms[formIdx].Questions.length - 1);
  };

  const handleDeleteQuestion = (formIdx: number, qIdx: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const newQ = { ...questionnaire };
    newQ.Forms[formIdx].Questions.splice(qIdx, 1);
    onUpdate(newQ);
    if (selectedFormIdx === formIdx && selectedQuestionIdx === qIdx) {
      setSelectedFormIdx(null);
      setSelectedQuestionIdx(null);
    }
  };

  const handleSaveInternal = async (currentQ?: any) => {
    if (!onSave) return;
    setIsSaving(true);
    try {
      await onSave(currentQ);
    } finally {
      setIsSaving(false);
    }
  };

  if (!questionnaire || !questionnaire.Forms) {
    return (
      <div className="p-20 text-center flex flex-col items-center gap-4 opacity-40">
        <EditIcon className="w-12 h-12 text-slate-200" />
        <p className="text-xs font-medium text-slate-400 leading-relaxed">
          Initializing questionnaire...
        </p>
      </div>
    );
  }

  return (
    <div className="flex gap-10 items-start">
      {/* Left Side: Form Preview / Question List */}
      <div className="w-1/2 space-y-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-[#1e2a32]">Teamhaven structure</h3>
          {onSave && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  const newQ = { ...questionnaire, Status: 3 };
                  onUpdate(newQ);
                  handleSaveInternal(newQ);
                }}
                disabled={questionnaire.Status === 3 || isSaving}
                className={`px-4 py-2 text-[11px] font-bold rounded-[3px] flex items-center gap-2 transition-colors ${questionnaire.Status === 3 ? 'bg-slate-100 text-slate-400' : 'bg-[rgb(25,34,41)] text-white hover:bg-slate-800'}`}
              >
                {questionnaire.Status === 3 ? <Check className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />}
                {questionnaire.Status === 3 ? 'Activated' : 'Activate Questionnaire'}
              </button>
              <button
                onClick={() => handleSaveInternal()}
                disabled={isSaving}
                className="px-4 py-2 bg-[#e2e8f0] text-[#475763] text-[11px] font-bold rounded-[3px] flex items-center gap-2 hover:bg-slate-300 transition-colors disabled:opacity-50"
              >
                {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                {isSaving ? 'Saving...' : 'Sync to TeamHaven'}
              </button>
            </div>
          )}
        </div>

        <div className="space-y-4">
          {questionnaire.Forms.map((form: any, fIdx: number) => (
            <div key={fIdx} className="bg-white rounded-[9px] overflow-hidden">
              <div className="bg-[#fbfbfa] px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Layout className="w-4 h-4 text-slate-400" />
                  <input
                    className="bg-transparent border-none p-0 text-[13px] font-bold text-[#1e2a32] focus:ring-0 w-48"
                    value={form.Caption?.DefaultCaption || ''}
                    onChange={(e) => {
                      const newQ = { ...questionnaire };
                      newQ.Forms[fIdx].Caption.DefaultCaption = e.target.value;
                      onUpdate(newQ);
                    }}
                  />
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleAddQuestion(fIdx)}
                    className="flex items-center gap-2 text-[11px] font-bold text-[#96c36d] hover:text-[#86b160] transition-colors"
                  >
                    <Plus className="w-3 h-3" /> Add question
                  </button>
                  <button
                    onClick={(e) => handleDeleteForm(fIdx, e)}
                    className="flex items-center gap-1 text-slate-300 hover:text-red-400 transition-colors"
                    title="Delete this form"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>

              <div className="p-2 space-y-1">
                {(form.Questions || []).map((q: any, qIdx: number) => {
                  const isSelected = selectedFormIdx === fIdx && selectedQuestionIdx === qIdx;
                  return (
                    <div
                      key={qIdx}
                      onClick={() => {
                        setSelectedFormIdx(fIdx);
                        setSelectedQuestionIdx(qIdx);
                      }}
                      className={`group flex items-center justify-between p-4 cursor-pointer rounded-[9px] transition-all ${isSelected
                        ? 'bg-[#1e2a32] text-white'
                        : 'hover:bg-slate-50 text-slate-600'
                        }`}
                    >
                      <div className="flex items-center gap-4">
                        <span className={`text-[10px] font-bold ${isSelected ? 'text-white/40' : 'text-slate-300'}`}>
                          {String(qIdx + 1).padStart(2, '0')}
                        </span>
                        <div className="flex flex-col gap-0.5">
                          <p className="text-xs font-bold truncate max-w-[200px]">
                            {q.Caption?.DefaultCaption || 'Untitled Question'}
                          </p>
                          <div className="flex items-center gap-2">
                            <span className={`text-[10px] font-semibold ${isSelected ? 'text-[#96c36d]' : 'text-slate-400'}`}>
                              {q.Type === 1 || q.Type === 2 ? 'Text'
                                : q.Type === 3 ? 'Dropdown'
                                  : q.Type === 4 ? 'Multi'
                                    : q.Type === 5 ? 'Numeric'
                                      : q.Type === 6 ? 'Yes/No'
                                        : q.Type === 10 ? 'Picture'
                                          : q.Type === 12 ? 'Signature'
                                            : 'Generic'}
                            </span>
                            {q.Required && (
                              <span className="text-[10px] font-semibold text-red-400">Required</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={(e) => handleDeleteQuestion(fIdx, qIdx, e)}
                          className={`p-1.5 transition-all outline-none ${isSelected ? 'text-white/20 hover:text-red-400' : 'opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-400'
                            }`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                        <ChevronRight className={`w-4 h-4 transition-transform ${isSelected ? 'translate-x-1' : 'text-slate-200'}`} />
                      </div>
                    </div>
                  );
                })}

                {(!form.Questions || form.Questions.length === 0) && (
                  <div className="p-10 text-center bg-[#fbfbfa] rounded-[9px]">
                    <p className="text-xs font-medium text-slate-300">No questions in this section</p>
                  </div>
                )}
              </div>
            </div>
          ))}

          {questionnaire.Forms.length === 0 && (
            <div className="bg-white rounded-[9px] p-20 flex flex-col items-center gap-6">
              <FileText className="w-8 h-8 text-slate-100" />
              <p className="text-xs text-slate-400 font-medium">No sections added yet</p>
              <button
                onClick={handleAddForm}
                className="bg-[#1e2a32] text-white px-6 py-2.5 rounded-[9px] text-[12px] font-bold hover:bg-slate-800 transition-colors"
              >
                Add first form
              </button>
            </div>
          )}

          {/* Add Form button — always visible when there are existing forms */}
          <button
            onClick={handleAddForm}
            className="w-full flex items-center justify-center gap-2 py-3 bg-[#fbfbfa] rounded-[9px] text-[12px] font-bold text-slate-400 hover:text-[#96c36d] transition-all"
          >
            <Plus className="w-3.5 h-3.5" />
            Add another form
          </button>
        </div>
      </div>

      {/* Right Side: Properties Panel */}
      <div className="w-1/2 sticky top-6">
        <div className="bg-white rounded-[9px] animate-in fade-in slide-in-from-right-4">
          <div className="bg-[#fbfbfa] px-6 py-4 rounded-t-[9px] flex items-center justify-between">
            <h4 className="text-[13px] font-bold text-[#1e2a32]">Question properties</h4>
            <div className="flex items-center gap-3">
              <div className={`w-2 h-2 rounded-full ${questionnaire.Status === 4 ? 'bg-[#96c36d]' :
                questionnaire.Status === 2 ? 'bg-blue-400' : 'bg-amber-400'
                }`} />
              <select
                className="text-[11px] font-semibold rounded-[9px] px-2 py-1 bg-white text-slate-500 outline-none focus:ring-1 focus:ring-[#96c36d]"
                value={questionnaire.Status || 1}
                onChange={(e) => {
                  const newQ = { ...questionnaire };
                  newQ.Status = parseInt(e.target.value);
                  onUpdate(newQ);
                }}
              >
                <option value={1}>Draft</option>
                <option value={2}>Checked</option>
                <option value={4}>Active</option>
              </select>
            </div>
          </div>

          {selectedFormIdx !== null && selectedQuestionIdx !== null ? (
            <div className="p-6 space-y-6">
              {/* Selected Question Header */}
              <div className="pb-6">
                <p className="text-[10px] font-bold text-slate-300 mb-2">Editing position: {selectedQuestionIdx + 1}</p>
                <textarea
                  className="w-full text-sm font-bold text-[#1e2a32] bg-white border border-[#e9e9e9] rounded-[9px] p-3 focus:ring-1 focus:ring-[#96c36d] outline-none transition-all"
                  placeholder="Enter question caption..."
                  rows={3}
                  value={questionnaire.Forms[selectedFormIdx].Questions[selectedQuestionIdx].Caption?.DefaultCaption || ''}
                  onChange={(e) => {
                    const newQ = { ...questionnaire };
                    newQ.Forms[selectedFormIdx].Questions[selectedQuestionIdx].Caption.DefaultCaption = e.target.value;
                    onUpdate(newQ);
                  }}
                />
              </div>

              {/* Basic Config */}
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                <div className="col-span-2">
                  <label className={FIELD_LABEL_CLS}>Caption</label>
                  <input
                    type="text"
                    className={FIELD_CLS}
                    value={questionnaire.Forms[selectedFormIdx].Questions[selectedQuestionIdx].Caption?.DefaultCaption || ''}
                    onChange={(e) => {
                      const newQ = { ...questionnaire };
                      newQ.Forms[selectedFormIdx].Questions[selectedQuestionIdx].Caption.DefaultCaption = e.target.value;
                      onUpdate(newQ);
                    }}
                  />
                </div>

                <div className="col-span-2">
                  <label className={FIELD_LABEL_CLS}>Question type</label>
                  <select
                    className={FIELD_CLS}
                    value={questionnaire.Forms[selectedFormIdx].Questions[selectedQuestionIdx].Type}
                    onChange={(e) => {
                      const newQ = { ...questionnaire };
                      const targetType = parseInt(e.target.value);
                      newQ.Forms[selectedFormIdx].Questions[selectedQuestionIdx].Type = targetType;
                      if (targetType === 10) {
                        newQ.Forms[selectedFormIdx].Questions[selectedQuestionIdx].Resolution = '800x600'; // Default to SVGA
                      }
                      onUpdate(newQ);
                    }}
                  >
                    <option value={1}>Text</option>
                    <option value={2}>Text (Multi Line)</option>
                    <option value={3}>Single Choice</option>
                    <option value={4}>Multiple Choice</option>
                    <option value={5}>Numeric</option>
                    <option value={6}>Yes / No</option>
                    <option value={10}>Picture</option>
                    <option value={11}>Date & Time</option>
                    <option value={12}>Signature</option>
                  </select>
                </div>

                <div className="col-span-2">
                  <label className={FIELD_LABEL_CLS}>Help message</label>
                  <textarea
                    className={FIELD_CLS}
                    rows={2}
                    placeholder="Help text for the field force..."
                    value={questionnaire.Forms[selectedFormIdx].Questions[selectedQuestionIdx].Help?.DefaultCaption || ''}
                    onChange={(e) => {
                      const newQ = { ...questionnaire };
                      if (!newQ.Forms[selectedFormIdx].Questions[selectedQuestionIdx].Help) {
                        newQ.Forms[selectedFormIdx].Questions[selectedQuestionIdx].Help = { DefaultCaption: '' };
                      }
                      newQ.Forms[selectedFormIdx].Questions[selectedQuestionIdx].Help.DefaultCaption = e.target.value;
                      onUpdate(newQ);
                    }}
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="q-req-builder"
                    checked={questionnaire.Forms[selectedFormIdx].Questions[selectedQuestionIdx].Required}
                    onChange={(e) => {
                      const newQ = { ...questionnaire };
                      newQ.Forms[selectedFormIdx].Questions[selectedQuestionIdx].Required = e.target.checked;
                      onUpdate(newQ);
                    }}
                    className="w-4 h-4 rounded-[3px] border-slate-300 text-[#96c36d] focus:ring-[#96c36d]"
                  />
                  <label htmlFor="q-req-builder" className="text-[11px] font-semibold text-[#1e2a32]">Required</label>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="q-readonly-builder"
                    checked={questionnaire.Forms[selectedFormIdx].Questions[selectedQuestionIdx].ReadOnly}
                    onChange={(e) => {
                      const newQ = { ...questionnaire };
                      newQ.Forms[selectedFormIdx].Questions[selectedQuestionIdx].ReadOnly = e.target.checked;
                      onUpdate(newQ);
                    }}
                    className="w-4 h-4 rounded-[3px] border-slate-300 text-[#96c36d] focus:ring-[#96c36d]"
                  />
                  <label htmlFor="q-readonly-builder" className="text-[11px] font-semibold text-[#1e2a32]">Read only</label>
                </div>

                <div>
                  <label className={FIELD_LABEL_CLS}>Min length</label>
                  <input
                    type="number"
                    className={FIELD_CLS}
                    placeholder="None"
                  />
                </div>
                <div>
                  <label className={FIELD_LABEL_CLS}>Max length</label>
                  <input
                    type="number"
                    className={FIELD_CLS}
                    placeholder="None"
                  />
                </div>
              </div>

              {/* Order & Duplication */}
              <div className="flex items-center gap-2 pt-4">
                <button
                  onClick={() => {
                    const newQ = { ...questionnaire };
                    if (selectedQuestionIdx > 0) {
                      const item = newQ.Forms[selectedFormIdx].Questions.splice(selectedQuestionIdx, 1)[0];
                      newQ.Forms[selectedFormIdx].Questions.splice(selectedQuestionIdx - 1, 0, item);
                      onUpdate(newQ);
                      setSelectedQuestionIdx(selectedQuestionIdx - 1);
                    }
                  }}
                  className="p-2 bg-slate-50 rounded-[9px] text-slate-400 hover:bg-slate-100 transition-colors"
                >
                  <ArrowUp className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => {
                    const newQ = { ...questionnaire };
                    const questions = newQ.Forms[selectedFormIdx].Questions;
                    if (selectedQuestionIdx < questions.length - 1) {
                      const item = questions.splice(selectedQuestionIdx, 1)[0];
                      questions.splice(selectedQuestionIdx + 1, 0, item);
                      onUpdate(newQ);
                      setSelectedQuestionIdx(selectedQuestionIdx + 1);
                    }
                  }}
                  className="p-2 bg-slate-50 rounded-[9px] text-slate-400 hover:bg-slate-100 transition-colors"
                >
                  <ArrowDown className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => {
                    const newQ = { ...questionnaire };
                    const currentQ = newQ.Forms[selectedFormIdx].Questions[selectedQuestionIdx];
                    const copy = JSON.parse(JSON.stringify(currentQ));
                    newQ.Forms[selectedFormIdx].Questions.splice(selectedQuestionIdx + 1, 0, copy);
                    onUpdate(newQ);
                    setSelectedQuestionIdx(selectedQuestionIdx + 1);
                  }}
                  className="p-2 bg-slate-50 rounded-[9px] text-slate-400 hover:bg-slate-100 transition-colors"
                >
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Type specific fields: Picture Resolution */}
              {questionnaire.Forms[selectedFormIdx].Questions[selectedQuestionIdx].Type === 10 && (
                <div className="pt-6 animate-in fade-in">
                  <label className={FIELD_LABEL_CLS}>Resolution</label>
                  <select
                    className={FIELD_CLS}
                    value={questionnaire.Forms[selectedFormIdx].Questions[selectedQuestionIdx].Resolution || '800x600'}
                    onChange={(e) => {
                      const newQ = { ...questionnaire };
                      newQ.Forms[selectedFormIdx].Questions[selectedQuestionIdx].Resolution = e.target.value;
                      onUpdate(newQ);
                    }}
                  >
                    <option value="640x480">VGA (640 x 480)</option>
                    <option value="800x600">SVGA (800 x 600)</option>
                    <option value="1024x768">XGA (1024 x 768)</option>
                    <option value="1280x1024">1MP (1280 x 1024)</option>
                    <option value="1600x1200">2MP (1600 x 1200)</option>
                    <option value="2048x1536">3MP (2048 x 1536)</option>
                    <option value="2365x1774">4MP (2365 x 1774)</option>
                    <option value="3264x2448">8MP (3264 x 2448)</option>
                  </select>
                </div>
              )}

              {/* Type specific fields: Choices for List types */}
              {(questionnaire.Forms[selectedFormIdx].Questions[selectedQuestionIdx].Type === 3 ||
                questionnaire.Forms[selectedFormIdx].Questions[selectedQuestionIdx].Type === 4) && (
                  <div className="pt-6 space-y-4 animate-in fade-in">
                    <div className="flex items-center justify-between">
                      <label className="text-[11px] font-bold text-[#1e2a32]">Choices</label>
                      <span className="text-[10px] font-semibold text-slate-300">{(questionnaire.Forms[selectedFormIdx].Questions[selectedQuestionIdx].Choices?.length || 0)} options</span>
                    </div>
                    <div className="bg-[#fbfbfa] p-4 rounded-[9px] space-y-3">
                      {questionnaire.Forms[selectedFormIdx].Questions[selectedQuestionIdx].Choices?.map((choice: any, cIdx: number) => (
                        <div key={cIdx} className="flex items-center gap-2">
                          <input
                            className="flex-1 text-xs bg-white rounded-[9px] px-3 py-2 focus:ring-1 focus:ring-[#96c36d] outline-none transition-all"
                            value={choice.Caption?.DefaultCaption || ''}
                            placeholder={`Choice ${cIdx + 1}`}
                            onChange={(e) => {
                              const newQ = { ...questionnaire };
                              newQ.Forms[selectedFormIdx].Questions[selectedQuestionIdx].Choices[cIdx].Caption.DefaultCaption = e.target.value;
                              onUpdate(newQ);
                            }}
                          />
                          <button
                            onClick={() => {
                              const newQ = { ...questionnaire };
                              newQ.Forms[selectedFormIdx].Questions[selectedQuestionIdx].Choices.splice(cIdx, 1);
                              onUpdate(newQ);
                            }}
                            className="text-slate-300 hover:text-red-400"
                          >
                            <CloseIcon className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => {
                          const newQ = { ...questionnaire };
                          if (!newQ.Forms[selectedFormIdx].Questions[selectedQuestionIdx].Choices) {
                            newQ.Forms[selectedFormIdx].Questions[selectedQuestionIdx].Choices = [];
                          }
                          newQ.Forms[selectedFormIdx].Questions[selectedQuestionIdx].Choices.push({
                            Caption: { DefaultCaption: '' },
                            Reference: null
                          });
                          onUpdate(newQ);
                        }}
                        className="w-full py-2.5 bg-[#96c36d] text-white text-[11px] font-bold rounded-[9px] hover:bg-[#86b160] transition-colors"
                      >
                        Add choice
                      </button>
                    </div>
                  </div>
                )}

              <div className="pt-4 flex items-center justify-between">
                <button
                  onClick={() => {
                    setSelectedFormIdx(null);
                    setSelectedQuestionIdx(null);
                  }}
                  className="text-[11px] font-semibold text-slate-400 hover:text-slate-600"
                >
                  Close editor
                </button>
              </div>
            </div>
          ) : (
            <div className="p-20 text-center flex flex-col items-center gap-4 opacity-40">
              <EditIcon className="w-12 h-12 text-slate-200" />
              <p className="text-xs font-medium text-slate-400 leading-relaxed">
                Select a question from the left <br /> to configure properties
              </p>
            </div>
          )}
        </div>

        <div className="mt-6 bg-blue-50 p-5 rounded-[9px] flex items-start gap-3">
          <AlertCircle className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
          <p className="text-[12px] font-medium text-blue-700 leading-relaxed">
            Design changes are synced live to TeamHaven. Ensure all required binding fields are set if using grid forms.
          </p>
        </div>
      </div>
    </div>
  );
};